import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Grid, Divider, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Collapse, IconButton,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { fetchProject, fetchEvaluationsByProject, deleteProjectGoal } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import Alert from "@mui/material/Alert";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";

const formatDate = (iso) => {
  if (!iso) return "";
  return iso.substring(0, 10).replace(/-/g, "/");
};

const formatCurrency = (value) => {
  if (value === undefined || value === null) return "-";
  return `¥${Number(value).toLocaleString()}`;
};

function parseWorkCostDescription(description) {
  if (!description) return null;
  try {
    return JSON.parse(description);
  } catch {
    return null;
  }
}

// 1行コスト表示
function CostRow({ label, amount, indent = false, bold = false }) {
  return (
    <Box sx={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      py: 0.4, pl: indent ? 3 : 0,
    }}>
      <Typography variant="body2" sx={{ fontWeight: bold ? "bold" : "normal", color: indent ? "text.secondary" : "text.primary" }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: bold ? "bold" : "normal", minWidth: 80, textAlign: "right" }}>
        {amount != null ? formatCurrency(amount) : "-"}
      </Typography>
    </Box>
  );
}

// 期間別コスト内訳パネル
function PeriodCostPanel({ evaluation }) {
  const [open, setOpen] = useState(false);
  const detail = parseWorkCostDescription(evaluation.evaluationDescription);
  const totalCost = evaluation.evaluationNumber;

  const periodLabel = detail?.checkActPeriod
    ? `${formatDate(detail.checkActPeriod.startDate)} 〜 ${formatDate(detail.checkActPeriod.endDate)}`
    : detail?.period || formatDate(evaluation.createdAt) || "-";

  const individuals = detail?.individuals || [];
  const customerIndividuals = individuals.filter((i) => i.type === "顧客");
  const productIndividuals = individuals.filter((i) => i.type === "製品");
  const partnerIndividuals = individuals.filter((i) => i.type === "パートナー");

  const sumIndividuals = (arr) =>
    arr.length > 0 ? arr.reduce((s, i) => s + (i.cost || 0), 0) : null;

  const memberBreakdown = detail?.memberBreakdown || [];

  return (
    <Paper variant="outlined" sx={{ mb: 1 }}>
      {/* 期間ヘッダー */}
      <Box
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1, cursor: "pointer" }}
        onClick={() => setOpen((v) => !v)}
      >
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>{periodLabel}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {formatCurrency(totalCost)}
          </Typography>
          <IconButton size="small">{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
        </Box>
      </Box>

      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 1 }}>
          <Divider sx={{ mb: 1 }} />

          <CostRow label="顧客カテゴリコスト" amount={detail?.customerCategoryCost} />
          <CostRow label="顧客コスト" amount={customerIndividuals.length > 0 ? sumIndividuals(customerIndividuals) : null} />
          {customerIndividuals.map((ind, i) => (
            <CostRow key={i} label={[ind.number, ind.name].filter(Boolean).join("・") || "-"} amount={ind.cost} indent />
          ))}
          {customerIndividuals.length === 0 && <CostRow label="顧客番号・顧客名" amount={null} indent />}

          <Divider sx={{ my: 0.5 }} />
          <CostRow label="製品カテゴリコスト" amount={detail?.productCategoryCost} />
          <CostRow label="製品コスト" amount={productIndividuals.length > 0 ? sumIndividuals(productIndividuals) : null} />
          {productIndividuals.map((ind, i) => (
            <CostRow key={i} label={[ind.number, ind.name].filter(Boolean).join("・") || "-"} amount={ind.cost} indent />
          ))}
          {productIndividuals.length === 0 && <CostRow label="製品シリアルNo・名称" amount={null} indent />}

          <Divider sx={{ my: 0.5 }} />
          <CostRow label="パートナーカテゴリコスト" amount={detail?.partnerCategoryCost} />
          <CostRow label="パートナーコスト" amount={partnerIndividuals.length > 0 ? sumIndividuals(partnerIndividuals) : null} />
          {partnerIndividuals.map((ind, i) => (
            <CostRow key={i} label={[ind.number, ind.name].filter(Boolean).join("・") || "-"} amount={ind.cost} indent />
          ))}
          {partnerIndividuals.length === 0 && <CostRow label="パートナー番号・名称" amount={null} indent />}

          <Divider sx={{ my: 0.5 }} />
          <CostRow label="データカテゴリコスト" amount={detail?.dataCategoryCost} />

          <Divider sx={{ my: 0.5 }} />
          <CostRow label="メンバーコスト" amount={detail?.memberCostTotal} />
          {memberBreakdown.length > 0 ? memberBreakdown.map((mb, i) => (
            <Box key={i} sx={{ pl: 3, py: 0.3, display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                {mb.workerName || mb.workerId || "-"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 60, textAlign: "right" }}>
                {mb.expendedTimeMs != null ? `${(mb.expendedTimeMs / 3600000).toFixed(2)}h` : "-"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, textAlign: "right" }}>
                {mb.resourceRate != null ? `¥${mb.resourceRate.toLocaleString()}/h` : "-"}
              </Typography>
              <Typography variant="body2" sx={{ minWidth: 80, textAlign: "right" }}>
                {formatCurrency(mb.cost)}
              </Typography>
            </Box>
          )) : (
            <CostRow label="ワーカー名・時間・レート・金額" amount={null} indent />
          )}

          <Divider sx={{ my: 0.5 }} />
          <CostRow label="アプリケーションコスト" amount={detail?.applicationCost} />

          <Divider sx={{ my: 0.5 }} />
          <CostRow label="財務資産コスト" amount={detail?.assetCostTotal} />

          <Divider sx={{ my: 0.5 }} />
          <CostRow label="合計" amount={totalCost} bold />
        </Box>
      </Collapse>
    </Paper>
  );
}

export default function ProjectGoalDetailPage() {
  const { businessDomainId, businessUnitId, projectId, goalId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/projects/${projectId}`;

  const [goal, setGoal] = useState(null);
  const [latestActualValue, setLatestActualValue] = useState(null);
  const [latestActualDate, setLatestActualDate] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isUsedByAchievements, setIsUsedByAchievements] = useState(false);

  // 作業コスト（全期間）
  const [costEvaluations, setCostEvaluations] = useState([]);
  const [costExpanded, setCostExpanded] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const d = await fetchProject(projectId);
      const found = (d.goals || [])[Number(goalId)];
      setGoal(found || null);

      const indicatorName = found?.indicator?.indicatorName;
      if (indicatorName) {
        const matched = (d.achievements || [])
          .filter((a) => a.goal?.indicator?.indicatorName === indicatorName)
          .sort((a, b) => {
            if (!a.actualDate && !b.actualDate) return 0;
            if (!a.actualDate) return 1;
            if (!b.actualDate) return -1;
            return new Date(b.actualDate) - new Date(a.actualDate);
          });
        setIsUsedByAchievements(matched.length > 0);
        if (matched.length > 0) {
          setLatestActualValue(matched[0].actualValue ?? null);
          setLatestActualDate(matched[0].actualDate || null);
        }
      }
    } catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, goalId]);

  const loadCost = useCallback(async () => {
    try {
      const data = await fetchEvaluationsByProject(projectId);
      const costs = (Array.isArray(data) ? data : [])
        .filter((e) => e.evaluationName === "Workコスト")
        .sort((a, b) => {
          // CheckAct期間のstartDateで昇順ソート（古い順）
          const getStart = (e) => {
            try {
              const d = JSON.parse(e.evaluationDescription);
              return d?.checkActPeriod?.startDate || e.createdAt || "";
            } catch { return e.createdAt || ""; }
          };
          return getStart(a) < getStart(b) ? -1 : 1;
        });
      setCostEvaluations(costs);
    } catch {
      // コスト取得失敗は非致命的
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadCost(); }, [loadCost]);

  const handleDelete = async () => {
    try {
      await deleteProjectGoal(projectId, goal);
      notify.success(t("message.delete.success"));
      navigate(`${basePath}/goals`);
    } catch (err) { notify.apiError(err); }
  };

  if (!goal) return null;

  // 累計コスト
  const cumulativeTotal = costEvaluations.length > 0
    ? costEvaluations.reduce((s, e) => s + (e.evaluationNumber || 0), 0)
    : null;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("project.goal.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" startIcon={<EditIcon />}
            disabled={isUsedByAchievements}
            onClick={() => navigate(`${basePath}/goals/${goalId}/edit`)}>
            {t("common.edit")}
          </Button>
          <Button variant="outlined" color="error" startIcon={<DeleteIcon />}
            disabled={isUsedByAchievements}
            onClick={() => setDeleteOpen(true)}>
            {t("common.delete")}
          </Button>
        </Box>
      </Box>
      {isUsedByAchievements && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          この目標には実績が登録されているため、編集・削除できません。
        </Alert>
      )}

      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12}>
            <DetailField label={t("project.goal.indicator")} value={goal.indicator?.indicatorName} />
            {goal.indicator && (
              <Paper variant="outlined" sx={{ mt: 1, p: 2, bgcolor: "grey.50" }}>
                {[
                  { label: t("appKpi.unit"),              value: goal.indicator.unit },
                  { label: t("appKpi.measuringMethod"),    value: goal.indicator.measuringMethod },
                  { label: t("appKpi.evaluationStandard"), value: goal.indicator.evaluationStandard },
                  { label: t("appKpi.description"),        value: goal.indicator.description },
                ].filter((row) => row.value).map((row) => (
                  <Box key={row.label} sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>{row.label}:</Typography>
                    <Typography variant="body2">{row.value}</Typography>
                  </Box>
                ))}
              </Paper>
            )}
          </Grid>

          {/* 計画値 */}
          <Grid size={12}>
            <DetailField
              label={t("project.goal.plannedValue")}
              value={goal.plannedValue != null && goal.plannedValue !== ""
                ? `${goal.plannedValue}${goal.indicator?.unit ? `（${goal.indicator.unit}）` : ""}`
                : ""}
            />
          </Grid>

          {/* 実績値（最新） */}
          {latestActualValue != null && (
            <Grid size={12}>
              <DetailField
                label={t("project.achievement.actualValue")}
                value={`${latestActualValue}${goal.indicator?.unit ? `（${goal.indicator.unit}）` : ""}${latestActualDate ? `　${formatDate(latestActualDate)}` : ""}`}
              />
            </Grid>
          )}

          <Grid size={12}>
            <DetailField label={t("project.goal.plannedDate")} value={formatDate(goal.plannedDate)} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("project.goal.deadline")} value={formatDate(goal.deadline)} />
          </Grid>
        </Grid>
      </DetailSection>

      {/* 作業コスト（常に表示・期間別） */}
      <>
        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <AccountBalanceWalletIcon color="primary" fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: "0.875rem" }}>
            作業コスト
          </Typography>
          <Chip
            label={cumulativeTotal != null ? `累計 ${formatCurrency(cumulativeTotal)}` : "-"}
            color={cumulativeTotal != null ? "primary" : "default"}
            size="small"
            sx={{ fontWeight: "bold" }}
          />
          <IconButton size="small" onClick={() => setCostExpanded((v) => !v)}>
            {costExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={costExpanded}>
          {costEvaluations.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              作業コストデータが未登録です。CheckActワークフローを実行すると自動的に集計されます。
            </Typography>
          ) : (
            <Box>
              {costEvaluations.map((ev) => (
                <PeriodCostPanel key={ev.evaluationId || ev.createdAt} evaluation={ev} />
              ))}
              {costEvaluations.length > 1 && (
                <Box sx={{ display: "flex", justifyContent: "space-between", px: 1, pt: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>累計</Typography>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {formatCurrency(cumulativeTotal)}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Collapse>
      </>

      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button variant="outlined" startIcon={<EmojiEventsIcon />}
          onClick={() => navigate(`${basePath}/goals/${goalId}/achievements`)}>
          {t("project.achievement.list.title")}
        </Button>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate(`${basePath}/goals`)}>{t("common.back")}</Button>
      </Box>

      <ConfirmDialog
        open={deleteOpen}
        onConfirm={handleDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </Box>
  );
}
